// pages/PerformancePage.jsx
import { useState, useEffect } from 'react';
import performanceController from '../../controllers/performanceController';
import Performance from '../../models/Performance';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown,
  Star, TrendingUp, Users, Award, Target, Send, CheckCircle
} from 'lucide-react';

const PerformancePage = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    reviewPeriod: '',
    reviewDate: new Date().toISOString().split('T')[0],
    reviewerId: '',
    technicalSkills: 0,
    communication: 0,
    teamwork: 0,
    leadership: 0,
    punctuality: 0,
    strengths: '',
    areasOfImprovement: '',
    goals: '',
    comments: '',
    status: 'Draft'
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('reviewDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const reviewPeriods = ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024', 'H1 2024', 'H2 2024', 'Annual 2024'];

  // Helper function to extract employee ID from object or string
  const getEmployeeId = (employeeId) => {
    if (!employeeId) return 'N/A';
    return typeof employeeId === 'object' ? employeeId.employeeId : employeeId;
  };

  // Helper function to extract reviewer ID from object or string
  const getReviewerId = (reviewerId) => {
    if (!reviewerId) return 'N/A';
    return typeof reviewerId === 'object' ? reviewerId.employeeId : reviewerId;
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    let result = [...reviews];
    
    if (searchQuery) {
      result = result.filter(r => {
        const empId = getEmployeeId(r.employeeId).toLowerCase();
        const revId = getReviewerId(r.reviewerId).toLowerCase();
        const period = r.reviewPeriod?.toLowerCase() || '';
        const query = searchQuery.toLowerCase();
        
        return empId.includes(query) || period.includes(query) || revId.includes(query);
      });
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(r => r.status === filterStatus);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      
      // Handle nested objects for sorting
      if (sortField === 'employeeId') {
        aVal = getEmployeeId(aVal);
        bVal = getEmployeeId(bVal);
      }
      if (sortField === 'reviewerId') {
        aVal = getReviewerId(aVal);
        bVal = getReviewerId(bVal);
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredReviews(result);
    setCurrentPage(1);
  }, [reviews, searchQuery, filterStatus, sortField, sortDirection]);

  const fetchReviews = async () => {
    setLoading(true);
    const result = await performanceController.getAll({ page: 1, limit: 100 });
    if (result.success) {
      setReviews(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? parseFloat(value) || 0 : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      
      // Auto-calculate overall rating when individual ratings change
      if (['technicalSkills', 'communication', 'teamwork', 'leadership', 'punctuality'].includes(name)) {
        updated.overallRating = performanceController.calculateOverallRating(updated);
      }
      
      return updated;
    });
    
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      employeeId: '',
      reviewPeriod: '',
      reviewDate: new Date().toISOString().split('T')[0],
      reviewerId: '',
      technicalSkills: 0,
      communication: 0,
      teamwork: 0,
      leadership: 0,
      punctuality: 0,
      strengths: '',
      areasOfImprovement: '',
      goals: '',
      comments: '',
      status: 'Draft'
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await performanceController.create(formData);
    
    if (result.success) {
      setShowAddModal(false);
      fetchReviews();
      alert('✅ Performance review created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleEdit = (review) => {
    setSelectedReview(review);
    setFormData({
      employeeId: getEmployeeId(review.employeeId),
      reviewPeriod: review.reviewPeriod,
      reviewDate: review.reviewDate,
      reviewerId: getReviewerId(review.reviewerId),
      technicalSkills: review.technicalSkills,
      communication: review.communication,
      teamwork: review.teamwork,
      leadership: review.leadership,
      punctuality: review.punctuality,
      strengths: review.strengths,
      areasOfImprovement: review.areasOfImprovement,
      goals: review.goals,
      comments: review.comments,
      status: review.status
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await performanceController.update(selectedReview.id, formData);
    
    if (result.success) {
      setShowEditModal(false);
      fetchReviews();
      alert('✅ Performance review updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleView = (review) => {
    setSelectedReview(review);
    setShowViewModal(true);
  };

  const handleDelete = async (review) => {
    const isConfirmed = window.confirm(`Delete performance review for ${review.reviewPeriod}?`);
    if (!isConfirmed) return;

    const result = await performanceController.delete(review.id);
    
    if (result.success) {
      fetchReviews();
      alert('✅ Performance review deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSubmit = async (review) => {
    const isConfirmed = window.confirm(`Submit review for ${review.reviewPeriod}?`);
    if (!isConfirmed) return;

    const result = await performanceController.submit(review.id);
    
    if (result.success) {
      fetchReviews();
      alert('✅ Review submitted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleComplete = async (review) => {
    const isConfirmed = window.confirm(`Mark review as completed?`);
    if (!isConfirmed) return;

    const result = await performanceController.complete(review.id);
    
    if (result.success) {
      fetchReviews();
      alert('✅ Review completed!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const result = await performanceController.export({ status: filterStatus }, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance-reviews-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Reviews exported!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        fill={i < rating ? '#f59e0b' : 'none'}
        color={i < rating ? '#f59e0b' : '#d1d5db'}
      />
    ));
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);

  const stats = {
    total: reviews.length,
    draft: reviews.filter(r => r.status === 'Draft').length,
    submitted: reviews.filter(r => r.status === 'Submitted').length,
    completed: reviews.filter(r => r.status === 'Completed').length,
    avgRating: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + r.overallRating, 0) / reviews.length).toFixed(2)
      : 0
  };

  const overallRating = performanceController.calculateOverallRating(formData);

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.2s' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
    ratingSection: { background: '#f8fafc', padding: '16px', borderRadius: '8px', marginTop: '16px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Performance Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Review
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Reviews</div>
            <Award size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Average Rating</div>
            <Star size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.avgRating}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Draft</div>
            <Edit2 size={24} color="#6b7280" />
          </div>
          <div style={{ ...styles.statValue, color: '#6b7280' }}>{stats.draft}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Submitted</div>
            <Send size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.submitted}</div>
        </div>

        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Completed</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.completed}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search reviews..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      {/* Reviews Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading reviews...
          </div>
        ) : currentReviews.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No reviews found. Click "Add Review" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('employeeId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Employee
                      {sortField === 'employeeId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('reviewPeriod')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Period
                      {sortField === 'reviewPeriod' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Reviewer</th>
                  <th style={styles.th} onClick={() => handleSort('overallRating')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Rating
                      {sortField === 'overallRating' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('reviewDate')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Review Date
                      {sortField === 'reviewDate' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentReviews.map((review) => (
                  <tr key={review.id} style={{ cursor: 'pointer' }} onClick={() => handleView(review)}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600' }}>
                        {getEmployeeId(review.employeeId)}
                      </div>
                    </td>
                    <td style={styles.td}>{review.reviewPeriod}</td>
                    <td style={styles.td}>
                      {getReviewerId(review.reviewerId)}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {getRatingStars(review.overallRating)}
                        </div>
                        <span style={{ fontWeight: '600', color: review.getRatingColor() }}>
                          {review.overallRating.toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td style={styles.td}>{new Date(review.reviewDate).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: `${review.getStatusColor()}20`, color: review.getStatusColor() }}>
                        {review.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleView(review); }} title="View">
                          <Eye size={18} color="#64748b" />
                        </button>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleEdit(review); }} title="Edit">
                          <Edit2 size={18} color="#3b82f6" />
                        </button>
                        {review.status === 'Draft' && (
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleSubmit(review); }} title="Submit">
                            <Send size={18} color="#f59e0b" />
                          </button>
                        )}
                        {review.status === 'Submitted' && (
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleComplete(review); }} title="Complete">
                            <CheckCircle size={18} color="#10b981" />
                          </button>
                        )}
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleDelete(review); }} title="Delete">
                          <Trash2 size={18} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredReviews.length)} of {filteredReviews.length} reviews
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add Performance Review' : 'Edit Performance Review'}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Employee ID *</label>
                    <input type="text" name="employeeId" style={styles.input} value={formData.employeeId} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Review Period *</label>
                    <select name="reviewPeriod" style={styles.input} value={formData.reviewPeriod} onChange={handleInputChange} required>
                      <option value="">Select Period</option>
                      {reviewPeriods.map(period => (
                        <option key={period} value={period}>{period}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Review Date *</label>
                    <input type="date" name="reviewDate" style={styles.input} value={formData.reviewDate} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Reviewer ID *</label>
                    <input type="text" name="reviewerId" style={styles.input} value={formData.reviewerId} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                      <option value="Draft">Draft</option>
                      <option value="Submitted">Submitted</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '8px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>Rating Criteria (0-5)</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Technical Skills</label>
                      <input type="number" name="technicalSkills" style={styles.input} value={formData.technicalSkills} onChange={handleInputChange} min="0" max="5" step="0.1" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Communication</label>
                      <input type="number" name="communication" style={styles.input} value={formData.communication} onChange={handleInputChange} min="0" max="5" step="0.1" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Teamwork</label>
                      <input type="number" name="teamwork" style={styles.input} value={formData.teamwork} onChange={handleInputChange} min="0" max="5" step="0.1" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Leadership</label>
                      <input type="number" name="leadership" style={styles.input} value={formData.leadership} onChange={handleInputChange} min="0" max="5" step="0.1" />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Punctuality</label>
                      <input type="number" name="punctuality" style={styles.input} value={formData.punctuality} onChange={handleInputChange} min="0" max="5" step="0.1" />
                    </div>
                  </div>
                </div>

                <div style={styles.ratingSection}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#475569' }}>Overall Rating (Auto-Calculated)</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {getRatingStars(overallRating)}
                    </div>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                      {overallRating.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Strengths</label>
                  <textarea name="strengths" style={styles.textarea} value={formData.strengths} onChange={handleInputChange} placeholder="List employee's key strengths..."></textarea>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Areas of Improvement</label>
                  <textarea name="areasOfImprovement" style={styles.textarea} value={formData.areasOfImprovement} onChange={handleInputChange} placeholder="List areas that need improvement..."></textarea>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Goals for Next Period</label>
                  <textarea name="goals" style={styles.textarea} value={formData.goals} onChange={handleInputChange} placeholder="Set goals for the next review period..."></textarea>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Additional Comments</label>
                  <textarea name="comments" style={styles.textarea} value={formData.comments} onChange={handleInputChange} placeholder="Any additional comments or notes..."></textarea>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Review' : 'Update Review')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedReview && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Performance Review - {selectedReview.reviewPeriod}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employee ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getEmployeeId(selectedReview.employeeId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Review Period</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedReview.reviewPeriod}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Reviewer ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getReviewerId(selectedReview.reviewerId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Review Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {new Date(selectedReview.reviewDate).toLocaleDateString()}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ ...styles.badge, backgroundColor: `${selectedReview.getStatusColor()}20`, color: selectedReview.getStatusColor() }}>
                    {selectedReview.status}
                  </span>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>Overall Rating</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {getRatingStars(selectedReview.overallRating)}
                  </div>
                  <span style={{ fontSize: '32px', fontWeight: '700', color: selectedReview.getRatingColor() }}>
                    {selectedReview.overallRating.toFixed(2)}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>Rating Breakdown</h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {[
                    { label: 'Technical Skills', value: selectedReview.technicalSkills },
                    { label: 'Communication', value: selectedReview.communication },
                    { label: 'Teamwork', value: selectedReview.teamwork },
                    { label: 'Leadership', value: selectedReview.leadership },
                    { label: 'Punctuality', value: selectedReview.punctuality }
                  ].map((item, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '14px', color: '#64748b' }}>{item.label}:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {getRatingStars(item.value)}
                        </div>
                        <span style={{ fontWeight: '600', minWidth: '30px', textAlign: 'right' }}>{item.value.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedReview.strengths && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Strengths</div>
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    {selectedReview.strengths}
                  </div>
                </div>
              )}

              {selectedReview.areasOfImprovement && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Areas of Improvement</div>
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    {selectedReview.areasOfImprovement}
                  </div>
                </div>
              )}

              {selectedReview.goals && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Goals for Next Period</div>
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    {selectedReview.goals}
                  </div>
                </div>
              )}

              {selectedReview.comments && (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Additional Comments</div>
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    {selectedReview.comments}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedReview); }}>
                <Edit2 size={16} /> Edit Review
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformancePage;